import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Check,
  Calendar,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import type { DayAvailability, AvailableSlot } from '@/types/agenda'
import { weekdayLabels } from '@/types/agenda'

interface TimeSlotPickerProps {
  availability: DayAvailability[]
  selectedSlot: AvailableSlot | null
  onSlotSelect: (slot: AvailableSlot) => void
  duration: number
  isLoading?: boolean
}

/**
 * Composant de sélection de créneau pour la prise de RDV client
 */
export function TimeSlotPicker({
  availability,
  selectedSlot,
  onSlotSelect,
  duration,
  isLoading,
}: TimeSlotPickerProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [weekOffset, setWeekOffset] = useState(0)

  // Filtrer les jours avec des créneaux disponibles
  const daysWithSlots = useMemo(() => {
    return availability.filter((day) => day.slots.length > 0)
  }, [availability])

  // Grouper par semaine
  const weekDays = useMemo(() => {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() + weekOffset * 7)
    startDate.setHours(0, 0, 0, 0)

    // Trouver le lundi de la semaine
    const dayOfWeek = startDate.getDay()
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
    startDate.setDate(startDate.getDate() + diff)

    const days: (DayAvailability | null)[] = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate)
      date.setDate(date.getDate() + i)
      const dayAvail = daysWithSlots.find(
        (d) => d.date.toDateString() === date.toDateString()
      )
      days.push(dayAvail || null)
    }

    return { days, startDate }
  }, [daysWithSlots, weekOffset])

  // Créneaux du jour sélectionné
  const selectedDaySlots = useMemo(() => {
    if (!selectedDate) return []
    const dayAvail = availability.find(
      (d) => d.date.toDateString() === selectedDate.toDateString()
    )
    return dayAvail?.slots || []
  }, [availability, selectedDate])

  // Navigation semaine
  const canGoPrev = weekOffset > 0
  const canGoNext = weekOffset < 4 // Max 4 semaines à l'avance

  const formatDateLabel = (date: Date) => {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
    })
  }

  const formatWeekRange = () => {
    const endDate = new Date(weekDays.startDate)
    endDate.setDate(endDate.getDate() + 6)
    return `${formatDateLabel(weekDays.startDate)} - ${formatDateLabel(endDate)}`
  }

  const isSlotSelected = (slot: AvailableSlot) => {
    return (
      selectedSlot?.start.getTime() === slot.start.getTime() &&
      selectedSlot?.end.getTime() === slot.end.getTime()
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-mint-600" />
      </div>
    )
  }

  if (daysWithSlots.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-12 h-12 mx-auto text-brown-300 mb-4" />
        <p className="text-brown-600">Aucun créneau disponible pour le moment.</p>
        <p className="text-sm text-brown-400 mt-2">
          Veuillez réessayer ultérieurement ou nous contacter directement.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Navigation semaine */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setWeekOffset((prev) => prev - 1)}
          disabled={!canGoPrev}
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>

        <span className="text-sm font-medium text-brown-700">
          {formatWeekRange()}
        </span>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setWeekOffset((prev) => prev + 1)}
          disabled={!canGoNext}
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Grille des jours */}
      <div className="grid grid-cols-7 gap-2">
        {weekDays.days.map((dayAvail, index) => {
          const date = new Date(weekDays.startDate)
          date.setDate(date.getDate() + index)
          const dayOfWeek = (index + 1) % 7 // 0 = Dimanche
          const isSelected = selectedDate?.toDateString() === date.toDateString()
          const hasSlots = dayAvail !== null
          const isToday = date.toDateString() === new Date().toDateString()

          return (
            <button
              key={index}
              onClick={() => hasSlots && setSelectedDate(date)}
              disabled={!hasSlots}
              className={cn(
                'p-3 rounded-lg text-center transition-all',
                hasSlots
                  ? isSelected
                    ? 'bg-mint-600 text-white shadow-md'
                    : 'bg-white border border-beige-200 hover:border-mint-400 hover:bg-mint-50'
                  : 'bg-beige-50 text-brown-300 cursor-not-allowed'
              )}
            >
              <div className="text-xs uppercase mb-1">
                {weekdayLabels[dayOfWeek].slice(0, 3)}
              </div>
              <div
                className={cn(
                  'text-lg font-semibold',
                  isToday && !isSelected && 'text-mint-600'
                )}
              >
                {date.getDate()}
              </div>
              {hasSlots && (
                <div
                  className={cn(
                    'text-xs mt-1',
                    isSelected ? 'text-mint-100' : 'text-mint-600'
                  )}
                >
                  {dayAvail.slots.length} dispo
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Créneaux du jour sélectionné */}
      <AnimatePresence mode="wait">
        {selectedDate && (
          <motion.div
            key={selectedDate.toDateString()}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-mint-600" />
              <span className="font-medium text-brown-900">
                {selectedDate.toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}
              </span>
            </div>

            {selectedDaySlots.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {selectedDaySlots.map((slot, index) => {
                  const isSelected = isSlotSelected(slot)
                  const timeLabel = slot.start.toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })

                  return (
                    <motion.button
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.02 }}
                      onClick={() => onSlotSelect(slot)}
                      className={cn(
                        'p-3 rounded-lg text-center transition-all flex items-center justify-center gap-2',
                        isSelected
                          ? 'bg-mint-600 text-white shadow-md ring-2 ring-mint-300'
                          : 'bg-white border border-beige-200 hover:border-mint-400 hover:bg-mint-50 text-brown-700'
                      )}
                    >
                      <Clock
                        className={cn(
                          'w-4 h-4',
                          isSelected ? 'text-white' : 'text-mint-600'
                        )}
                      />
                      <span className="font-medium">{timeLabel}</span>
                      {isSelected && <Check className="w-4 h-4" />}
                    </motion.button>
                  )
                })}
              </div>
            ) : (
              <p className="text-center text-brown-500 py-4">
                Aucun créneau disponible ce jour.
              </p>
            )}

            {/* Info durée */}
            <div className="text-center text-sm text-brown-500">
              <Clock className="w-4 h-4 inline-block mr-1" />
              Durée du rendez-vous : {duration} minutes
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Message si aucun jour sélectionné */}
      {!selectedDate && (
        <div className="text-center py-6 text-brown-500">
          <Calendar className="w-8 h-8 mx-auto mb-2 text-brown-300" />
          Sélectionnez une date pour voir les créneaux disponibles
        </div>
      )}
    </div>
  )
}

export default TimeSlotPicker
